import React, {useEffect, useMemo, useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import {useLocation} from 'react-router-dom';
import Button from '../../../../../components/form/button/button';
import {assets} from '../../../../../utils/assetsSrc';
import RiskAssessmentNestedTabs from '../../components/risk-assessment-nested-tabs/risk-assessment-nested-tabs';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  createTask,
  deleteTask,
  generateExcel,
  generateExcelId,
  getRiskAssessmentTasks,
  updateFilterCriteria,
  getDevices,
  createDevice,
  deleteDevice,
} from '../../../../../redux/risk-assessment-core/riskAssessmentCoreSlice';
import AddEditTask from '../../components/addEditTask/addEditTask';
import {useSelector} from 'react-redux';
import {
  getRiskAssessmentCoreLoadingState,
  getRiskAssessmentCoreTasksState,
  getTaskFilterDetails,
  getDevicesState,
} from '../../../../../redux/selectors/riskAssessmentCore';
import TaskCard from '../../components/task-card/task-card';
import AddDevice from '../../components/add-device/addDevice';
import DeviceCard from '../../components/device-card/device-card';
import Loader from '../../../../../components/loader/loader';
import './specific-risk-assessment.scss';
import {allRoutes} from '../../../../../utils/routeConstants';
import {
  assessmentTypeIds,
  lifeCyclePhasesEnum,
  rbac,
  sortingEnum,
  taskFrequencyEnum,
  taskStatusIDS,
  riskAssessmentStatusNames,
  taskStatusEnum,
  sortingFunctionalSafetyEnum,
} from '../../../../../utils/constants';
import {
  sameFilterValue,
  toFilterArray,
} from '../../../../../utils/filterUtils';
import {apiFunctions} from '../../../../../api/functions';
import Share from '../../components/share/share';
import CustomSearch from '../../../../../components/custom-search/customSearch';
import {getLoginStateSelector} from '../../../../../redux/selectors/loginSelector';
import Filter from '../../components/filter/filter';
import SingleSelectCheckmarks from '../../components/single-select-checkbox/singleSelectCheckBox';
import {toast} from 'react-toastify';

const MAX_FILTER_LABELS = 2;

export default function SpecificRiskAssessment() {
  const location = useLocation();
  const dispatch = useDispatch();
  const nav = useNavigate();
  const taskList = useSelector(getRiskAssessmentCoreTasksState());
  const deviceList = useSelector(getDevicesState());
  const isLoading = useSelector(getRiskAssessmentCoreLoadingState());
  const userRoleId = useSelector(getLoginStateSelector())?.loggedInUser?.role;
  const [riskAssessmentData, setRiskAssessmentData] = useState({});
  const [exportState, setExportState] = useState('Export');
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [taskToBeDuplicated, setTaskToBeDuplicated] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [taskSubCategories, setTaskSubCategories] = useState([]);
  const filterDetails = useSelector(getTaskFilterDetails());
  const isCra = riskAssessmentData?.type === assessmentTypeIds.cybersecurity;

  const [intervalId, setIntervalId] = useState();
  const [excelDetails, setExcelDetails] = useState();
  const [excelIdDetails, setExcelIdDetails] = useState();
  const [download, setDownload] = useState(false);
  const [isTopBarMinimized, setIsTopBarMinimized] = useState(false); 
  const [riskAssessmentConfig, setRiskAssessmentConfig] = useState({
    params: {
      search: '',
      life_cycle_phase: [],
      task_sub_categories: [],
      status: [],
      ordering: '',
    },
  });
  let searchTimer;

  const lifeCyclePhaseLabels = useMemo(
    () =>
      toFilterArray(riskAssessmentConfig?.params.life_cycle_phase)
        .map(id => lifeCyclePhasesEnum.find(item => item.id === id)?.name)
        .filter(Boolean),
    [riskAssessmentConfig?.params.life_cycle_phase],
  );

  const taskSubCategoryLabels = useMemo(
    () => toFilterArray(riskAssessmentConfig?.params.task_sub_categories),
    [riskAssessmentConfig?.params.task_sub_categories],
  );

  const statusLabels = useMemo(
    () =>
      toFilterArray(riskAssessmentConfig?.params.status)
        .map(id => taskStatusEnum.find(statusObj => statusObj.id === id)?.name)
        .filter(Boolean),
    [riskAssessmentConfig?.params.status],
  );

  const filtersApplied = !!(
    lifeCyclePhaseLabels.length ||
    taskSubCategoryLabels.length ||
    statusLabels.length
  );


  const [loading, setLoading] = useState(true);

  // updating based on persisted filter data
  useEffect(() => {
    if (filterDetails?.filterCriteria) {
      setRiskAssessmentConfig(prevState => {
        const updatedParams = {...prevState.params};
        let changed = false;

        for (const key in filterDetails.filterCriteria) {
          const next = toFilterArray(filterDetails.filterCriteria[key]);
          if (!sameFilterValue(next, updatedParams[key])) {
            updatedParams[key] = next;
            changed = true;
          }
        }
        return changed ? {...prevState, params: updatedParams} : prevState;
      });
    }
    setLoading(false);
  }, [filterDetails]);

  useEffect(() => {
    const raData = location?.state?.selectedRiskAssessment;
    setRiskAssessmentData(raData);
    // fetchTaskList(location?.state?.selectedRiskAssessment?.id)
    if (raData?.type !== assessmentTypeIds.cybersecurity) {
      apiFunctions.getTaskSubCategoryList().then(resp => {
        // endpoint returns a list of distinct sub-category strings actually used on tasks
        const list = (resp?.data || [])
          .filter(Boolean)
          .map(value => ({id: value, name: value}));
        setTaskSubCategories(list);
      });
    }
  }, []);

  const fetchTaskList = raId => {
    const payload = {
      riskAssessmentId: raId,
      config: riskAssessmentConfig,
    };
    dispatch(getRiskAssessmentTasks(payload));
  };

  useEffect(() => {
    if(loading) return;
    if(isCra) return;
    fetchTaskList(
      location?.state?.selectedRiskAssessment?.id,
      riskAssessmentConfig,
    );
  }, [riskAssessmentConfig,loading,isCra]);

  const fetchDeviceList = craId => {
    dispatch(getDevices({craId}));
  };

  useEffect(() => {
    if(loading) return;
    if(!isCra) return;
    fetchDeviceList(location?.state?.selectedRiskAssessment?.id);
  }, [loading, isCra]);

    /* restore last choice from localStorage */
  useEffect(() => {
    const stored = localStorage.getItem('isTopBarMinimized');
    if (stored === 'true') setIsTopBarMinimized(true);
  }, []);

  /* persist every change */
  useEffect(() => {
    localStorage.setItem('isTopBarMinimized', isTopBarMinimized);
  }, [isTopBarMinimized]);

  const toggleTopBar = () => setIsTopBarMinimized(prev => !prev); 

  const handleTaskCreation = payload => {
    dispatch(
      createTask({
        payload,
        callback: resp => {
          if (resp?.status === 201)
            toast.success(riskAssessmentData?.type == 1 ? 'Task successfully saved.' : 'Machine Control System successfully saved.', {
              autoClose: 2000,
              className: 'toast-success',
            });
          else
          toast.error(riskAssessmentData?.type == 1 ? 'Unable to save the task. Please try again.' : 'Unable to save the Machine Control System. Please try again.', {
            autoClose: 2000,
            className: 'toast-error',
          });

          setShowAddTask(false);
          setShowEditTask(false);
          fetchTaskList(riskAssessmentData.id);
        },
      }),
    );
  };

  const handleTaskSelection = taskData => {
    nav(allRoutes.dashboardRoutes.assessmentRoutes.taskDetails, {
      state: {
        ...location.state,
        selectedTask: taskData,
      },
    });
  };

  const handleTaskDelete = id => {
    dispatch(
      deleteTask({
        id,
        callback: resp => {
          fetchTaskList(riskAssessmentData.id);
        },
      }),
    );
  };

  const handleDeviceCreation = values => {
    dispatch(
      createDevice({
        payload: {
          risk_assessment: riskAssessmentData.id,
          ...values,
        },
        callback: deviceData => {
          setShowAddDevice(false);
          nav(allRoutes.dashboardRoutes.assessmentRoutes.deviceDetails, {
            state: {...location.state, selectedDevice: deviceData},
          });
        },
      }),
    );
  };

  const handleDeviceSelection = deviceData => {
    nav(allRoutes.dashboardRoutes.assessmentRoutes.deviceDetails, {
      state: {
        ...location.state,
        selectedDevice: deviceData,
      },
    });
  };

  const handleDeviceDelete = id => {
    dispatch(
      deleteDevice({
        id,
        callback: resp => {
          fetchDeviceList(riskAssessmentData.id);
        },
      }),
    );
  };

  const handleTaskToBeDuplicated = taskData => {
    const values = {
      taskName: taskData.name,
      riskAssessmentList: taskData.risk_assessment_detail,
      taskDetails: taskData.details,
      lifeCyclePhases: lifeCyclePhasesEnum.find(
        el => el.id === taskData.life_cycle_phase,
      ),
      //subCategories: {id:taskData.sub_categories,name:taskData.sub_category_name},
      task_sub_categories: taskData.task_sub_categories,
      taskFrequency: taskFrequencyEnum.find(el => el.id === taskData.frequency),
      liveWork: taskData.live_work,
      status:
        taskStatusEnum.find(el => el.id === taskData.task_status) ||
        taskStatusEnum[0],
    };
    setTaskToBeDuplicated(values);
  };

  const exportClickHandler = () => {
    if (exportState === 'Download') {
      setDownload(true);
    } else {
      setDownload(false);
      const payload = {
        risk_assessment_id: riskAssessmentData.id,
      };
      dispatch(
        generateExcelId({
          payload,
          callback: resp => {
            setExcelIdDetails(resp);
          },
        }),
      );
      setExportState('Preparing...');
    }
  };

  useEffect(() => {
    if (excelIdDetails) {
      let interval = setInterval(
        () =>
          dispatch(
            generateExcel({
              payload: {
                id: excelIdDetails?.unique_id,
              },
              callback: resp => {
                setExcelDetails(resp);
              },
            }),
          ),
        2000,
      );
      setIntervalId(interval);
    }
  }, [excelIdDetails]);

  useEffect(() => {
    if (excelDetails?.status === 'Completed') {
      setExportState('Download');
      clearInterval(intervalId);
    } else if (excelDetails?.status === 'Failed') {
      setExportState('Export');
      clearInterval(intervalId);
      setExcelIdDetails(undefined);
    }
  }, [excelDetails]);

  const handleInputChange = e => {
    clearTimeout(searchTimer);
    const {value} = e.target;
    searchTimer = setTimeout(() => {
      setRiskAssessmentConfig(prev => ({
        ...prev,
        params: {...prev.params, search: value},
      }));
    }, 1000);
  };

  const handleOrderingChange = e => {
    setRiskAssessmentConfig(prev => ({
      ...prev,
      params: {...prev.params, ordering: e.id},
    }));
  };

  const handleFilterChange = payload => {
    dispatch(updateFilterCriteria(payload));
    setShowFilter(false);
    setRiskAssessmentConfig(prev => ({
      ...prev,
      params: {
        ...prev.params,
        life_cycle_phase: toFilterArray(payload.life_cycle_phase),
        task_sub_categories: toFilterArray(payload.task_sub_categories),
        status: toFilterArray(payload.status),
      },
    }));
  };

  const displayActiveFiltersHandler = () => {
    const summarise = labels =>
      labels.length > MAX_FILTER_LABELS
        ? `${labels.slice(0, MAX_FILTER_LABELS).join(', ')} +${
            labels.length - MAX_FILTER_LABELS
          }`
        : labels.join(', ');

    const filters = [
      {label: 'Life cycle phases', value: summarise(lifeCyclePhaseLabels)},
      {label: 'Task subcategory', value: summarise(taskSubCategoryLabels)},
      {label: 'Status', value: summarise(statusLabels)},
    ];

    return (
      <>
        {filtersApplied && (
          <div
            style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
            {filters
              .filter(filter => filter.value)
              .map((filter, index, filteredArray) => (
                <div key={index}>
                  {filter.value}
                  {index < filteredArray.length - 1 && ', '}
                </div>
              ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="specific-risk-assessment-wrapper">
      {/* NEW wrapper that can be collapsed */}
      <div className={`top-bar-container ${isTopBarMinimized ? 'minimized' : ''}`}>
        {!isCra && (
        <header>
        <div className="progress-header">
          {`Progress: ${
            taskList?.filter(
              item => item.task_status === taskStatusIDS.completed,
            )?.length ?? 0
          }/${taskList?.length ?? 0} ${location.state?.type === 1 ? "task" : "control system"} assessed`}
          {isTopBarMinimized && (
            <span className="material-symbols-sharp expand-arrow"
            style={{marginLeft: '8px'}}   // << add this
            title="Expand"
            onClick={toggleTopBar}>
        arrow_drop_down
      </span>

            )}
        </div>
        <div className="pl-search">
          <CustomSearch
            onChange={handleInputChange}
            width="400px"
            type="text"
          />
          <div className="sorting">
            <SingleSelectCheckmarks
              data={sortingEnum}
              handleOrderingChange={handleOrderingChange}
            />
          </div>
          <div
            className={`filter ${filtersApplied ? 'active' : ''}`}
            onClick={() => {
              setShowFilter(true);
            }}>
            <img
              src={assets.filterIcon}
              alt="filter icon"
              className={filtersApplied ? 'active-icon' : ''}
            />
            Filter
          </div>
        </div>
        <div>{displayActiveFiltersHandler()}</div>
      </header>
        )}
      {isCra && (
        <div className="progress-header">
          {`Progress: ${
            (Array.isArray(deviceList) ? deviceList : []).filter(
              item => item.derived?.complete,
            )?.length ?? 0
          }/${(Array.isArray(deviceList) ? deviceList : []).length ?? 0} devices assessed`}
          {isTopBarMinimized && (
            <span
              className="material-symbols-sharp expand-arrow"
              style={{marginLeft: '8px'}}
              title="Expand"
              onClick={toggleTopBar}>
              arrow_drop_down
            </span>
          )}
        </div>
      )}
      <article>
        <div className="assessment-details">
          <Row>
            <Col className="details" xs={5}>
              <div className="heading">{riskAssessmentData?.name}</div>
              <div className="pl-list">
                Product-line: {riskAssessmentData?.product_detail?.name}
              </div>
              <div className="pm-list">
                Model:{' '}
                {riskAssessmentData?.machine_detail
                  ?.map(el => el.name)
                  .join(', ')}
              </div>
            </Col>
            <Col xs={7}>
              <div className="ra-actions">
                <a href={excelDetails?.file} download={download}>
                  <button className="export-btn" onClick={exportClickHandler}>
                    <img style={{marginRight: '15px'}} src={assets.excelIcon} />
                    {exportState}
                  </button>
                </a>
                <Button
                  variant="secondary-button"
                  clickHandler={() => setShowSharePopup(true)}>
                  <img style={{marginRight: '15px'}} src={assets.shareIcon} />
                  Share
                </Button>
                {!isCra && rbac.addTask(userRoleId) && (
                  <Button
                    variant="submit-button"
                    clickHandler={() => setShowAddTask(true)}>
                    <img
                      style={{marginRight: '15px'}}
                      src={assets.plusCircle}
                    />
                    {riskAssessmentData?.type == 1 ? 'Add Task' : 'Add Function'}
                  </Button>
                )}
                {isCra && rbac.addTask(userRoleId) && (
                  <Button
                    variant="submit-button"
                    clickHandler={() => setShowAddDevice(true)}>
                    <img
                      style={{marginRight: '15px'}}
                      src={assets.plusCircle}
                    />
                    Add Device
                  </Button>
                )}
              </div>
            </Col>
          </Row>
          <Row></Row>
        </div>
        <div className="nested-tabs">
          <RiskAssessmentNestedTabs />
        </div>
      </article>
      {/* pinned arrow at bottom (only when expanded) */}
      <div className="toggle-arrow" onClick={toggleTopBar}>
        <span className="material-symbols-sharp" title="Minimize">
          arrow_drop_up
        </span>
      </div>
    </div>
      <main>
        {!isCra && taskList.map(task => (
          <TaskCard
            key={task.id}
            data={task}
            handleTaskSelection={() => handleTaskSelection(task)}
            handleTaskDelete={() => handleTaskDelete(task.id)}
            handleTaskDuplicate={() => {
              setShowEditTask(true);
              handleTaskToBeDuplicated(task);
            }}
            assessmentType={riskAssessmentData?.type}
          />
        ))}
        {isCra && (Array.isArray(deviceList) ? deviceList : []).map(device => (
          <DeviceCard
            key={device.id}
            data={device}
            handleDeviceSelection={() => handleDeviceSelection(device)}
            handleDeviceDelete={() => handleDeviceDelete(device.id)}
          />
        ))}
      </main>
      {!isCra && (
        <AddEditTask
          label={riskAssessmentData?.type == 1 ? 'New Task' : 'New Function'}
          show={showAddTask}
          setShow={setShowAddTask}
          handleTaskCreation={handleTaskCreation}
          assessmentType={riskAssessmentData?.type}
        />
      )}
      {!isCra && showEditTask && (
        <AddEditTask
          label="Duplicate Risk Task"
          show={showEditTask}
          setShow={setShowEditTask}
          prefilledData={taskToBeDuplicated}
          handleTaskCreation={handleTaskCreation}
          assessmentType={riskAssessmentData?.type}
        />
      )}
      {isCra && (
        <AddDevice
          show={showAddDevice}
          setShow={setShowAddDevice}
          handleDeviceCreation={handleDeviceCreation}
        />
      )}
      {showSharePopup && (
        <Share
          show={showSharePopup}
          setShow={setShowSharePopup}
          riskAssessmentId={location?.state?.selectedRiskAssessment?.id}
          linkName={riskAssessmentData?.name}
        />
      )}
      {!isCra && showFilter && (
        <Filter
          label="Filter"
          show={showFilter}
          setShow={setShowFilter}
          handleFilterChange={handleFilterChange}
          config={riskAssessmentConfig.params}
          taskSubCategories={taskSubCategories}
        />
      )}
      {isLoading && <Loader />}
    </div>
  );
}
